import { Link } from "react-router-dom";
import type { Melon } from "@/types/content";

interface MelonCardProps {
  melon: Melon;
  layout?: "grid" | "list";
}

function heatClass(heat: string) {
  if (heat === "爆") return "bg-accent-500 text-background-50";
  if (heat === "热") return "bg-secondary-500 text-background-50";
  if (heat === "新") return "bg-primary-500 text-background-50";
  return "bg-background-200 text-foreground-600";
}

export default function MelonCard({ melon, layout = "grid" }: MelonCardProps) {
  if (layout === "list") {
    return (
      <article className="group flex gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300 md:gap-5 md:p-4">
        <div className="h-24 w-32 shrink-0 overflow-hidden rounded-md bg-background-100 md:h-28 md:w-44">
          <img
            src={melon.cover}
            alt={melon.coverAlt}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="mb-1.5 flex flex-wrap items-center gap-2 text-xs">
            <span className="rounded-full bg-primary-100 px-2 py-0.5 font-medium text-primary-800">
              {melon.categoryName}
            </span>
            {melon.heat && (
              <span className={`rounded-full px-2 py-0.5 font-medium ${heatClass(melon.heat)}`}>
                {melon.heat}
              </span>
            )}
            <span className="text-foreground-400">{melon.date}</span>
          </div>
          <h3 className="font-heading text-base font-bold leading-snug text-foreground-950 md:text-lg">
            <Link to={`/article/${melon.id}`} className="transition-colors hover:text-primary-700">
              {melon.title}
            </Link>
          </h3>
          <p className="mt-1.5 line-clamp-2 text-sm text-foreground-600">{melon.summary}</p>
          <div className="mt-2 flex items-center gap-3 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-user-3-line" aria-hidden="true"></i>
              {melon.author}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line" aria-hidden="true"></i>
              {melon.readTime}
            </span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      <Link to={`/article/${melon.id}`} className="block h-44 w-full overflow-hidden bg-background-100 md:h-48">
        <img
          src={melon.cover}
          alt={melon.coverAlt}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
      </Link>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
          <span className="rounded-full bg-primary-100 px-2 py-0.5 font-medium text-primary-800">
            {melon.categoryName}
          </span>
          {melon.heat && (
            <span className={`rounded-full px-2 py-0.5 font-medium ${heatClass(melon.heat)}`}>
              {melon.heat}
            </span>
          )}
        </div>
        <h3 className="font-heading text-base font-bold leading-snug text-foreground-950 md:text-lg">
          <Link to={`/article/${melon.id}`} className="transition-colors hover:text-primary-700">
            {melon.title}
          </Link>
        </h3>
        <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-600">
          {melon.summary}
        </p>
        <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3 text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-user-3-line" aria-hidden="true"></i>
            {melon.author}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-time-line" aria-hidden="true"></i>
            {melon.readTime}
          </span>
        </div>
      </div>
    </article>
  );
}